/**
 * Universal video player.
 *
 * Persistence:
 *   The <video> element + the hls.js / mpegts.js engines live in a single
 *   module-level singleton, NOT inside a React component lifecycle. The
 *   <Player> component renders a placeholder div and (on mount) physically
 *   moves the singleton video element into it; on unmount it returns the
 *   element to a hidden "limbo" container. This means navigation between
 *   /watch and the floating mini-player does not tear down the underlying
 *   playback — the same video keeps playing, paused state is preserved,
 *   buffer is intact.
 *
 * Engine selection (MPEG-TS first, since 99% of upstream streams are TS):
 *   raw .ts (HTTP)         → mpegts.js
 *   .m3u8                  → hls.js (or native if Safari)
 *   .mp4 / .mkv / direct   → native <video>
 *
 * Transmux seek (MKV / HEVC movies):
 *   The transmux endpoint pipes a fragmented MP4 that the browser cannot
 *   byte-seek into. Pass `onSeek` + `baseTime` and the slider operates in
 *   "seek-via-reload" mode: the slider doesn't poke video.currentTime, it
 *   reports the target back to the parent which re-loads the URL with a
 *   `?seek=<seconds>` query — ffmpeg restarts from that timestamp.
 */
import { useEffect, useRef, useState } from "react";
import { useIsPhone } from "../lib/useIsPhone";
import { isCastCancel } from "../lib/castError";
import { desktopPip, isDesktop } from "../lib/desktop";
import { useMpvPlayback } from "../lib/useMpvPlayback";
import { streamSourceLabel } from "../lib/streamLabel";
import { DesktopVideoSurface } from "./DesktopVideoSurface";
import {
  AudioLines,
  Cast,
  Captions,
  ChevronsLeft,
  ChevronsRight,
  Gauge,
  Maximize,
  Pause,
  PictureInPicture2,
  Play,
  RotateCcw,
  Volume2,
  VolumeX,
} from "lucide-react";
import Hls from "hls.js";
import mpegts from "mpegts.js";

import { usePlayback, type PlaybackSubtitle } from "../contexts/PlaybackContext";

type EngineKind = "ts" | "hls" | "native";

interface Props {
  url: string;
  altUrl?: string | null;
  /** Force engine selection (overrides URL extension sniffing). Required when
   * `url` points to a same-origin proxy that hides the upstream extension. */
  engine?: EngineKind;
  /** Same-origin proxy URL for the alt source. Used in the iOS fallback path. */
  altEngine?: EngineKind;
  poster?: string | null;
  title?: string;
  autoPlay?: boolean;
  isLive?: boolean;
  /** Override the displayed duration (catchup, where the underlying TS stream
   * doesn't carry duration metadata so video.duration is meaningless). Seconds. */
  forceDuration?: number | null;
  /** Cast token for the active playlist — appended to cast media URLs so the
   * receiver (which doesn't have our cookie) can fetch authenticated streams. */
  castToken?: string | null;
  /** Override URL used when casting — typically a transcoded HLS endpoint
   * for live channels so the receiver gets H.264 + AAC regardless of the
   * upstream codec / container. */
  castUrl?: string | null;
  /** External WebVTT subtitle tracks. Each entry is appended as a <track>
   * element on the singleton video so the browser surfaces them via
   * video.textTracks (and our existing subtitle picker). */
  subtitles?: PlaybackSubtitle[];
  /** Render the control chrome (control bar + phone touch-gesture layer).
   * The mini player embeds a full Player inside a 112px thumbnail on phone,
   * where a complete control bar is unusable and its gesture layer swallows
   * taps meant for the mini player's own buttons. */
  chrome?: boolean;
  /** The raw upstream URL, for native playback in the desktop shell.
   * mpv plays this directly — no /transmux/, no proxy hop. Ignored in a
   * browser, where `window.brainzr` does not exist. */
  nativeUrl?: string | null;
  /** When set, clicking the Captions button delegates to this callback
   * (parent opens a richer subtitle browser modal) instead of using the
   * built-in inline picker. */
  onOpenSubsBrowser?: () => void;
  /** When set, the player adds this offset to displayed currentTime — used by
   * the transmux seek-via-reload flow where ffmpeg restarts at `seek=N`. */
  baseTime?: number;
  /** When set, slider seeking is delegated to the parent (commit-on-release
   * semantics). Used to trigger a stream reload at the requested time. */
  onSeek?: (target: number) => void;
  /** Set false to make the seek slider read-only (still shows progress).
   * Used for catchup TS streams where in-buffer seeking would break playback. */
  seekable?: boolean;
  /** Called when the player surfaces an error message — lets the parent
   * (WatchPage) react to e.g. HEVC failures by routing through the live
   * transcode endpoint. */
  onError?: (msg: string) => void;
  onProgress?: (currentTime: number, duration: number) => void;
  onEnded?: () => void;
  resumeAt?: number;
}

declare global {
  interface Window {
    cast?: {
      framework?: {
        CastContext: {
          getInstance(): {
            setOptions(opts: {
              receiverApplicationId: string;
              autoJoinPolicy: string;
              resumeSavedSession?: boolean;
            }): void;
            requestSession(): Promise<unknown>;
            getCurrentSession(): { loadMedia(req: unknown): Promise<void>; endSession(stopCasting: boolean): void } | null;
            getCastState(): string;
            addEventListener(eventType: string, handler: (e: unknown) => void): void;
            removeEventListener(eventType: string, handler: (e: unknown) => void): void;
          };
        };
        CastContextEventType: {
          SESSION_STATE_CHANGED: string;
          CAST_STATE_CHANGED: string;
        };
        SessionState: {
          NO_SESSION: string;
          SESSION_STARTING: string;
          SESSION_STARTED: string;
          SESSION_START_FAILED: string;
          SESSION_ENDING: string;
          SESSION_ENDED: string;
          SESSION_RESUMED: string;
        };
        CastState: {
          NO_DEVICES_AVAILABLE: string;
          NOT_CONNECTED: string;
          CONNECTING: string;
          CONNECTED: string;
        };
      };
    };
    chrome?: {
      cast?: {
        AutoJoinPolicy: { ORIGIN_SCOPED: string };
        media: {
          DEFAULT_MEDIA_RECEIVER_APP_ID: string;
          StreamType: { BUFFERED: string; LIVE: string };
          MediaInfo: new (contentId: string, contentType: string) => {
            metadata?: unknown;
            streamType?: string;
          };
          GenericMediaMetadata: new () => Record<string, unknown>;
          MetadataType: { GENERIC: number };
          LoadRequest: new (info: unknown) => unknown;
        };
        Image: new (url: string) => { url: string };
      };
    };
    __onGCastApiAvailable?: (avail: boolean) => void;
  }
}

const isIOS = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.userAgent.includes("Mac") && "ontouchend" in document);

const isSafari = () => /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

function detectKind(u: string, hint?: EngineKind): EngineKind {
  if (hint) return hint;
  const lower = u.toLowerCase().split("?")[0];
  if (lower.endsWith(".m3u8")) return "hls";
  if (lower.endsWith(".ts")) return "ts";
  if (/\.(mp4|mkv|mov|webm|m4v)$/.test(lower)) return "native";
  if (lower.includes("/live/")) return "ts";
  return "native";
}

/** mpegts.js runs IO in a blob-worker that has no `window.location`, so any
 * relative URL we hand it fails URL parsing. Resolve to an absolute URL up-front. */
function absolutize(u: string): string {
  if (!u) return u;
  if (/^[a-z]+:\/\//i.test(u) || u.startsWith("blob:") || u.startsWith("data:")) return u;
  if (typeof window === "undefined") return u;
  if (u.startsWith("/")) return window.location.origin + u;
  return new URL(u, window.location.href).toString();
}

// ─── Module-level singleton ─────────────────────────────────────────────
// One <video> element + one engine instance per app. Survives React
// component unmounts; only torn down when the user closes the mini-player.

interface EngineState {
  videoEl: HTMLVideoElement;
  limbo: HTMLDivElement;
  hls: Hls | null;
  mpegts: ReturnType<typeof mpegts.createPlayer> | null;
  loadedKey: string;  // "<engine>::<absoluteUrl>" — skip reload if same
  isLive: boolean;    // tracked so cast-exit can decide whether to re-arm the live stall watchdog
  /** Aborts every videoEl listener attached for the current load (preroll
   * + rebuffer hold). destroyEngines fires this so the singleton videoEl
   * doesn't accumulate stale listeners across stream swaps. */
  listenersAbort?: AbortController;
}

let _engine: EngineState | null = null;
// Tracks whether the user EXPLICITLY paused via the play/pause button. Lives
// outside React so its value survives Player remounts during navigation. When
// false (default + after any URL change), Player mounts will auto-resume if
// the video has gone paused for any non-user reason (browser auto-pause on
// reparent, MSE recovery, etc.).
let _userPaused = false;

/** Did the viewer deliberately pause? The mini player uses this to stay out of
 *  the way: a paused stream should not reappear as a docked bar, and above all
 *  should not resume itself. */
export function isUserPaused(): boolean {
  return _userPaused;
}

// True while a Chromecast session is active. Module-level so the LOADING_COMPLETE
// / ERROR handlers attached to the live mpegts.js engine (which run outside
// React) can refuse to call videoEl.play() during a cast — otherwise local
// audio would resume on the user's device while the TV is still playing.
let _isCasting = false;
function enterCastMode() {
  _isCasting = true;
  stopLiveStallWatchdog();
  const e = _engine;
  if (!e) return;
  // Pause local playback. We additionally unload mpegts.js so its fetcher
  // stops pulling bytes from the panel (saves bandwidth, and prevents the
  // EOF/error handlers from re-firing while the TV is the playback target).
  try { e.videoEl.pause(); } catch { /* ignore */ }
  try { e.mpegts?.unload(); } catch { /* ignore */ }
}

function exitCastMode() {
  _isCasting = false;
  const e = _engine;
  if (!e) return;
  if (e.mpegts) {
    try {
      e.mpegts.load();
      if (e.isLive) startLiveStallWatchdog();
    } catch { /* ignore */ }
  }
  if (!_userPaused) {
    e.videoEl.play().catch(() => { /* autoplay block — needs gesture */ });
  }
}

// Stall watchdog for live mpegts.js streams. Some panels send EOF or just
// hang silently after 10–15 min; mpegts.js doesn't auto-recover. We poll
// video.currentTime — if it stops advancing for 30 s while playback is
// supposed to be running, tear the engine down and reconnect.
let _liveStallWatchdog: number | null = null;
let _stallLastTime = -1;
let _stallLastChange = 0;

function startLiveStallWatchdog() {
  if (_liveStallWatchdog !== null) return;
  _stallLastTime = -1;
  _stallLastChange = Date.now();
  _liveStallWatchdog = window.setInterval(() => {
    const e = _engine;
    if (!e || !e.mpegts) {
      _stallLastTime = -1;
      _stallLastChange = Date.now();
      return;
    }
    const v = e.videoEl;
    if (v.paused) {
      _stallLastTime = -1;
      _stallLastChange = Date.now();
      return;
    }
    if (v.currentTime !== _stallLastTime) {
      _stallLastTime = v.currentTime;
      _stallLastChange = Date.now();
      return;
    }
    if (Date.now() - _stallLastChange > 30_000) {
      if (_isCasting) {
        // Cast owns playback; don't poke the local engine.
        _stallLastChange = Date.now();
        return;
      }
      try {
        e.mpegts.unload();
        e.mpegts.load();
        v.play().catch(() => {});
      } catch { /* engine torn down */ }
      _stallLastChange = Date.now();
    }
  }, 5_000);
}

function stopLiveStallWatchdog() {
  if (_liveStallWatchdog !== null) {
    clearInterval(_liveStallWatchdog);
    _liveStallWatchdog = null;
  }
}

function getEngine(): EngineState {
  if (_engine) return _engine;
  const v = document.createElement("video");
  v.playsInline = true;
  v.controls = false;
  v.setAttribute("playsinline", "");
  v.setAttribute("x-webkit-airplay", "allow");
  v.style.cssText = "width:100%;height:100%;background:#000;object-fit:contain;display:block;";
  const limbo = document.createElement("div");
  limbo.id = "__player-limbo";
  limbo.style.cssText =
    "position:fixed;left:-99999px;top:-99999px;width:1px;height:1px;visibility:hidden;pointer-events:none;";
  limbo.appendChild(v);
  document.body.appendChild(limbo);
  _engine = { videoEl: v, limbo, hls: null, mpegts: null, loadedKey: "", isLive: false };
  return _engine;
}

function destroyEngines(e: EngineState) {
  stopLiveStallWatchdog();
  e.hls?.destroy();
  e.hls = null;
  e.mpegts?.destroy();
  e.mpegts = null;
  // Aborts the preroll progress listener + the rebuffer waiting/progress
  // pair attached for the previous load.
  e.listenersAbort?.abort();
  e.listenersAbort = undefined;
}

// HEVC / unsupported codec detection. mpegts.js / hls.js bubble the underlying
// MSE addSourceBuffer error string up to us — match the codec hint and rephrase
// it so the user understands the problem isn't transient.
function isHevcCodecError(msg: string | undefined | null): boolean {
  if (!msg) return false;
  const m = msg.toLowerCase();
  if (m.includes("hvc1") || m.includes("hev1") || m.includes("hevc") || m.includes("h.265") || m.includes("h265")) return true;
  // Generic MSE rejection — in our environment the only codecs upstream
  // sends that aren't H.264 are HEVC, so a generic "unsupported addSourceBuffer"
  // is almost certainly HEVC. Treating it as such triggers the transcode
  // fallback rather than a cryptic dead-end.
  if (m.includes("addsourcebuffer") && m.includes("unsupported")) return true;
  if (m.includes("media format type is not supported")) return true;
  return false;
}

function isHevcCodecString(codec: string | undefined | null): boolean {
  if (!codec) return false;
  const c = codec.toLowerCase();
  return c.startsWith("hvc1") || c.startsWith("hev1") || c.includes("hevc");
}

// Chrome MSE doesn't accept audio/mp4;codecs=ac-3 or eac-3 — only Edge with
// the Dolby extension and a handful of OEM Chromiums do. When the source
// audio is AC-3 / E-AC-3, addSourceBuffer fires:
//   "The type provided ('audio/mp4;codecs=ac-3') is unsupported."
// and playback stalls. Detection paths: mpegts.js MEDIA_INFO carries the
// audioCodec ("ac-3" / "ec-3"), and the addSourceBuffer error string
// itself names the codec.
function isAc3CodecString(codec: string | undefined | null): boolean {
  if (!codec) return false;
  const c = codec.toLowerCase().trim();
  return c === "ac-3" || c === "ac3" || c === "ec-3" || c === "eac3" ||
         c.startsWith("ac-3") || c.startsWith("ec-3");
}

function isAc3CodecError(msg: string | undefined | null): boolean {
  if (!msg) return false;
  const m = msg.toLowerCase();
  return m.includes("ac-3") || m.includes("ac3") || m.includes("ec-3") || m.includes("eac3");
}

// HE-AAC v1 (mp4a.40.5: AAC LC + SBR) and v2 (mp4a.40.29: AAC LC + SBR + PS)
// are nominally part of the MPEG-4 audio family but Chromium's MSE
// implementation rejects them on a number of platforms (Linux Chrome
// without the Widevine bundle, some Android Chrome builds, older
// Chromebooks). The rejection surfaces either as a `bufferAppendError`
// after addSourceBuffer "succeeded" or as `MEDIA_ERR_DECODE` once the
// first audio frame hits the decoder.
//
// Detection paths (mirrors the AC-3 plumbing):
//   • mpegts.js MEDIA_INFO event carries audioCodec="mp4a.40.5" / "40.29"
//     OR mimeType including those substrings.
//   • Generic error strings (bufferAppendError + audio context, or the
//     mime string itself) are caught as a backstop.
function isHeAacCodecString(codec: string | undefined | null): boolean {
  if (!codec) return false;
  const c = codec.toLowerCase().trim();
  // mp4a.40.5 = HE-AAC v1 (SBR), mp4a.40.29 = HE-AAC v2 (SBR + PS).
  return c === "mp4a.40.5" || c === "mp4a.40.29" ||
         c.startsWith("mp4a.40.5") || c.startsWith("mp4a.40.29");
}

function isHeAacCodecError(msg: string | undefined | null): boolean {
  if (!msg) return false;
  const m = msg.toLowerCase();
  if (m.includes("mp4a.40.5") || m.includes("mp4a.40.29")) return true;
  // hls.js surfaces audio decoder problems via `bufferAppendError` with no
  // codec string; treat that as HE-AAC-likely when we know the source is
  // a panel with HE-AAC content. Caller (WatchPage handleError) is the
  // arbiter of "should we transcode" — we just flag it as a possible
  // codec problem rather than letting the UI dead-end.
  return false;
}

function loadInto(
  e: EngineState,
  url: string,
  kind: EngineKind,
  isLive: boolean,
  setError: ((m: string) => void) | null,
) {
  // Belt-and-braces engine override: an HLS playlist URL must always be
  // handled by hls.js, regardless of the kind passed in. This protects
  // against any upstream confusion in pickEngine / detectKind that could
  // otherwise route a /playlist.m3u8 URL into the mpegts.js (TS) branch
  // and trip its StartupStallJumper on a non-live source.
  // Note: catchup is a non-live TS pipe — mpegts.js handles it correctly via
  // `isLive: false` + `lazyLoad: true` (configured below). Don't downgrade
  // ts → native for !isLive: native <video> can't decode raw .ts on any
  // browser, and there's no transmux fallback for catchup.
  const lower = url.toLowerCase().split("?")[0];
  if (lower.includes("/playlist.m3u8") || lower.endsWith(".m3u8")) {
    kind = "hls";
  }

  const key = `${kind}::${url}`;
  if (key === e.loadedKey) return; // same source already loaded — leave it alone
  // URL change implies new content, seek-via-reload, or transcode fallback —
  // any pause carried over from before is no longer meaningful.
  _userPaused = false;
  destroyEngines(e);
  e.videoEl.removeAttribute("src");
  e.videoEl.load();  // force reset
  e.isLive = isLive;

  // Stop the live-stream watchdog whenever we leave the ts engine; the
  // ts branch will start it again if needed.
  stopLiveStallWatchdog();

  if (kind === "hls") {
    if (Hls.isSupported() && !isSafari()) {
      const hls = new Hls({
        // For our live HLS feed (transmux output) we EXPLICITLY don't want
        // low-latency mode — that would chase the live edge and keep the
        // buffer thin. We'd rather build a 60s cushion and tolerate ~5–10s
        // behind realtime; the IPTV viewer doesn't care about glass-to-glass
        // latency, they care about not stuttering.
        lowLatencyMode: false,
        backBufferLength: isLive ? 30 : 90,
        // Live HLS gets the same 60s forward target as VOD — our sliding
        // window is 12 segments × 4s = 48s on disk, so 60s nominal lets
        // hls.js fill aggressively when there's headroom available.
        maxBufferLength: 60,
        enableWorker: true,
      });
      e.hls = hls;
      hls.loadSource(url);
      hls.attachMedia(e.videoEl);
      // Recovery escalator for live HLS. Live transmux output from a flaky
      // panel often produces small SPS/PPS variations across ffmpeg
      // respawns — MSE rejects those appends with bufferAppendError. The
      // earlier "give up after 2 recoverMediaError() calls" was too eager
      // and surfaced "Unable to play" while the stream was still alive.
      // Now we escalate gradually:
      //   1st fatal MEDIA_ERROR → swapAudioCodec() + recoverMediaError()
      //   2nd, 3rd → recoverMediaError() again
      //   4th → jump to live edge and recover (skips a sticky bad segment)
      //   beyond that → surrender with explicit error
      // The counter resets every time a fragment buffers successfully, so
      // a single bad patch doesn't spend all the retries.
      let mediaRecoverCount = 0;
      const MAX_MEDIA_RECOVER = 5;
      let swappedAudioCodec = false;
      hls.on(Hls.Events.FRAG_BUFFERED, () => {
        // Successful append — reset the recovery counter so a transient
        // bad segment doesn't permanently consume the budget.
        mediaRecoverCount = 0;
      });
      hls.on(Hls.Events.ERROR, (_ev, data) => {
        if (data.fatal) {
          // hls.js error data carries `details`, `error.message`, etc. — sniff for HEVC.
          const errMsg = (data.error?.message || data.details || "") as string;
          if (isHevcCodecError(errMsg)) {
            setError?.(
              "This channel uses HEVC (H.265) video, which Chrome on this device can't decode. " +
              "Try Microsoft Edge or Safari, or a Mac/iPhone where HEVC is hardware-supported."
            );
            return;
          }
          if (data.type === Hls.ErrorTypes.NETWORK_ERROR) hls.startLoad();
          else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
            if (mediaRecoverCount >= MAX_MEDIA_RECOVER) {
              try { hls.destroy(); } catch { /* ignore */ }
              setError?.(
                `Playback failed (${data.details || "media error"}). The stream's audio or video can't be decoded by this browser.`
              );
              return;
            }
            mediaRecoverCount += 1;
            // 1st attempt: swap audio codec — fixes mismatches between init
            // segment and fragment audio config (HE-AAC ↔ AAC LC, etc.).
            // Then recover. After the swap, fall through to plain recover.
            if (!swappedAudioCodec) {
              swappedAudioCodec = true;
              try { hls.swapAudioCodec(); } catch { /* ignore */ }
              try { hls.recoverMediaError(); } catch { /* ignore */ }
              return;
            }
            // 4th attempt and beyond on live: jump past whatever fragment
            // is sticky. hls.js keeps retrying the same frag every recover;
            // forcing a seek to the live edge makes it skip ahead.
            if (isLive && mediaRecoverCount >= 4) {
              try {
                const buf = e.videoEl.buffered;
                if (buf && buf.length > 0) {
                  // Jump to ~3s before the latest buffered point.
                  e.videoEl.currentTime = Math.max(0, buf.end(buf.length - 1) - 3);
                }
                hls.startLoad();
              } catch { /* ignore */ }
              return;
            }
            try { hls.recoverMediaError(); } catch { /* ignore */ }
          }
        }
      });
    } else {
      e.videoEl.src = url;
    }
  } else if (kind === "ts") {
    if (mpegts.getFeatureList().mseLivePlayback) {
      const player = mpegts.createPlayer(
        { type: "mpegts", isLive: isLive, url, cors: true },
        {
          enableWorker: true,
          enableStashBuffer: true,
          // Live: 8192 KB (~8 MB) of pre-demux cushion. We pair this with
          // a preroll hold below so we don't start playback on a thin MSE
          // buffer that the first jitter spike drains. Memory cost: ~8 MB
          // per active live stream — negligible on phones, free on desktop.
          // Catchup stays at 768 KB — non-live, hits its own seek-via-reload
          // flow.
          stashInitialSize: isLive ? 8192 : 768,
          lazyLoad: !isLive,
          liveBufferLatencyChasing: false,
          liveBufferLatencyMaxLatency: 30,
          liveBufferLatencyMinRemain: 10,
          autoCleanupSourceBuffer: true,
          autoCleanupMaxBackwardDuration: 60,
          autoCleanupMinBackwardDuration: 30,
        }
      );
      e.mpegts = player;
      player.attachMediaElement(e.videoEl);
      player.load();
      // mpegts.js MEDIA_INFO event fires once per metadata-arrived with the
      // detected codec strings. Catching HEVC / AC-3 here lets us flip to
      // the transcode endpoint BEFORE the addSourceBuffer rejection happens.
      // We also unload() the player immediately so mpegts.js doesn't keep
      // marching toward an addSourceBuffer that we know will fail — that
      // was the source of the noisy "Failed to execute 'addSourceBuffer'"
      // console errors.
      const stopMpegtsForCodecFallback = () => {
        try { player.unload(); } catch { /* ignore */ }
      };
      player.on(mpegts.Events.MEDIA_INFO, (mi: unknown) => {
        const m = mi as { videoCodec?: string; audioCodec?: string; mimeType?: string };
        if (isHevcCodecString(m?.videoCodec) || isHevcCodecError(m?.mimeType)) {
          stopMpegtsForCodecFallback();
          setError?.(
            "This channel uses HEVC (H.265) video. Switching to the transcoded fallback…"
          );
          return;
        }
        if (isAc3CodecString(m?.audioCodec) || isAc3CodecError(m?.mimeType)) {
          stopMpegtsForCodecFallback();
          setError?.(
            "This channel uses AC-3 audio (Chrome can't decode it natively). Switching to the transcoded fallback…"
          );
          return;
        }
        if (isHeAacCodecString(m?.audioCodec) || isHeAacCodecError(m?.mimeType)) {
          stopMpegtsForCodecFallback();
          setError?.(
            "This channel uses HE-AAC audio (mp4a.40.5/29 — Chrome can't decode it on this device). Switching to the transcoded fallback…"
          );
        }
      });
      // ERROR is a backstop for when MEDIA_INFO didn't fire (or the codec
      // string was unrecognised). The MSE addSourceBuffer rejection string
      // surfaces here verbatim. Live channels also get bounded reconnect
      // for transient network errors; once the cap is hit we surface a
      // real error to the user instead of silently retrying forever.
      const RECONNECT_MAX = 3;
      const RECONNECT_WINDOW_MS = 30_000;
      let reconnectAttempts = 0;
      let reconnectWindowStart = Date.now();
      player.on(mpegts.Events.ERROR, (_t, info, detail) => {
        const msg = typeof info === "string" ? info : "";
        const detailMsg = (detail as { msg?: string; code?: number } | undefined)?.msg || "";
        const detailCode = (detail as { code?: number } | undefined)?.code;
        if (isHevcCodecError(msg) || isHevcCodecError(detailMsg)) {
          stopMpegtsForCodecFallback();
          setError?.(
            "This channel uses HEVC (H.265) video. Switching to the transcoded fallback…"
          );
          return;
        }
        if (isAc3CodecError(msg) || isAc3CodecError(detailMsg)) {
          stopMpegtsForCodecFallback();
          setError?.(
            "This channel uses AC-3 audio (Chrome can't decode it natively). Switching to the transcoded fallback…"
          );
          return;
        }
        if (isHeAacCodecError(msg) || isHeAacCodecError(detailMsg)) {
          stopMpegtsForCodecFallback();
          setError?.(
            "This channel uses HE-AAC audio (mp4a.40.5/29 — Chrome can't decode it on this device). Switching to the transcoded fallback…"
          );
          return;
        }
        if (isLive) {
          // Bounded reconnect with explicit user feedback once exhausted.
          // Reset the counter if more than RECONNECT_WINDOW_MS has passed
          // since the last burst — that way a brief blip doesn't burn a
          // retry that should be available later.
          const now = Date.now();
          if (now - reconnectWindowStart > RECONNECT_WINDOW_MS) {
            reconnectAttempts = 0;
            reconnectWindowStart = now;
          }
          reconnectAttempts += 1;
          if (reconnectAttempts > RECONNECT_MAX) {
            const upstreamHint = detailCode === 502 || /\b502\b/.test(msg + detailMsg)
              ? "panel responded with 502 (upstream busy or unavailable)"
              : "upstream connection failed";
            setError?.(
              `This channel isn't reachable right now — ${upstreamHint}. Try again in a minute, switch to Proxied/Direct in settings, or pick a different channel.`
            );
            try { player.unload(); } catch { /* ignore */ }
            return;
          }
          setTimeout(() => {
            // While casting the local engine is unloaded on purpose; do not
            // reconnect or local audio would resume on the user's device.
            if (_isCasting) return;
            try {
              player.unload();
              player.load();
              e.videoEl.play().catch(() => {});
            } catch { /* engine already torn down */ }
          }, 1500);
        }
      });
      // LOADING_COMPLETE fires when the loader has read past EOF. For VOD
      // that's normal (file ended); for live it means the panel sent EOF —
      // probably a connection age-out, panel-side max-connections kick, or
      // a flaky CDN. We try to reconnect once or twice transparently, but
      // if the panel keeps EOFing within a short window we flip to the
      // server-side transmux fallback. ffmpeg's `-reconnect` flags keep
      // the upstream alive across these blips and the HLS sliding window
      // means the client sees one continuous stream instead of a
      // "play-3s, drop, reconnect, play-3s, drop…" stutter loop.
      const PANEL_EOF_MAX = 2;
      const PANEL_EOF_WINDOW_MS = 30_000;
      let panelEofCount = 0;
      let panelEofWindowStart = Date.now();
      player.on(mpegts.Events.LOADING_COMPLETE, () => {
        if (!isLive) return;
        const now = Date.now();
        if (now - panelEofWindowStart > PANEL_EOF_WINDOW_MS) {
          panelEofCount = 0;
          panelEofWindowStart = now;
        }
        panelEofCount += 1;
        if (panelEofCount > PANEL_EOF_MAX) {
          stopMpegtsForCodecFallback();
          setError?.(
            "Panel keeps dropping the upstream connection (multiple EOFs in a row). Switching to the transcoded fallback for a stable stream…"
          );
          return;
        }
        setTimeout(() => {
          if (_isCasting) return;
          try {
            player.unload();
            player.load();
            e.videoEl.play().catch(() => {});
          } catch { /* ignore */ }
        }, 1500);
      });
      if (isLive) {
        startLiveStallWatchdog();
        // Both the preroll hold AND the mid-stream rebuffer hold attach
        // listeners on the singleton videoEl. They have to go away cleanly
        // when this engine is torn down (channel change, mode flip, etc.)
        // so the next stream doesn't get phantom resume calls. AbortController
        // gives us a single switch — destroyEngines aborts and every
        // listener registered with this signal unbinds.
        e.listenersAbort?.abort();
        e.listenersAbort = new AbortController();
        const { signal } = e.listenersAbort;

        // ─── Preroll hold ────────────────────────────────────────────
        // Live MPEG-TS arrives in bursts (panels send a few seconds at a
        // time, ffmpeg encode pipeline adds variance). Without a hold the
        // browser starts playback the moment MSE has anything — often
        // under a second of video — and the first quiet window drains it.
        // Hold play() until ~3s are buffered. 5s fallback so a slow
        // upstream still starts.
        const PREROLL_TARGET = 3;
        let prerolled = false;
        const onPrerollProgress = () => {
          if (prerolled) return;
          try {
            const b = e.videoEl.buffered;
            if (!b || b.length === 0) return;
            const ahead = b.end(b.length - 1) - e.videoEl.currentTime;
            if (ahead >= PREROLL_TARGET) {
              prerolled = true;
              window.clearTimeout(fallbackTimer);
              if (!_userPaused && !_isCasting) {
                e.videoEl.play().catch(() => { /* autoplay block — needs gesture */ });
              }
            }
          } catch { /* ignore */ }
        };
        const fallbackTimer = window.setTimeout(() => {
          if (prerolled) return;
          prerolled = true;
          if (!_userPaused && !_isCasting) {
            e.videoEl.play().catch(() => { /* ignore */ });
          }
        }, 5000);
        // Cancel the fallback timer if the engine is destroyed before
        // preroll completes — otherwise it could fire late and start
        // playing the *next* stream that's been swapped in.
        signal.addEventListener("abort", () => window.clearTimeout(fallbackTimer), { once: true });
        e.videoEl.addEventListener("progress", onPrerollProgress, { signal });
        // Note: the previous mid-stream rebuffer hold was removed for
        // live MPEG-TS. Pausing a live source on `waiting` causes the
        // MSE source buffer to drain entirely (live data is being fed
        // at realtime, so the buffer can't fill while paused). When we
        // then resume, mpegts.js's StartupStallJumper sees currentTime
        // far behind the live edge, seeks forward, the buffer is empty
        // again, `waiting` fires again, and we get the "skip ahead /
        // skip back / buffer" loop the user reported. Letting the
        // browser handle stalls naturally for live is the right call;
        // the preroll above already gives a healthy starting cushion.
      }
    } else {
      e.videoEl.src = url;
    }
  } else {
    e.videoEl.src = url;
  }
  e.loadedKey = key;
}

export function Player({
  url,
  altUrl,
  engine,
  altEngine,
  poster,
  title,
  autoPlay = true,
  isLive = false,
  forceDuration,
  castToken,
  castUrl,
  subtitles,
  chrome = true,
  nativeUrl,
  onOpenSubsBrowser,
  baseTime = 0,
  onSeek,
  seekable = true,
  onError,
  onProgress,
  onEnded,
  resumeAt,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [volume, setVolumeState] = useState<number>(() => {
    if (typeof window === "undefined") return 1;
    const v = parseFloat(localStorage.getItem("brainzr.volume") || "1");
    return Number.isFinite(v) && v >= 0 && v <= 1 ? v : 1;
  });
  const [progress, setProgress] = useState({ current: 0, duration: 0 });
  const [error, setErrorState] = useState<string | null>(null);
  // True when our autoplay attempt was blocked by the browser policy
  // (typically only after a hard refresh — there's no user-gesture chain).
  // We render a centered "Tap to play" overlay so the user has an obvious
  // single-click recovery path; tapping it provides the gesture and starts
  // playback.
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  const onErrorRef = useRef<typeof onError>(onError);
  onErrorRef.current = onError;
  const setError = (msg: string | null) => {
    setErrorState(msg);
    if (msg) onErrorRef.current?.(msg);
  };
  const [castAvailable, setCastAvailable] = useState(false);
  // Declared above the mpv hook: casting may be initiated from the global
  // cast picker rather than this player, and mpv must stand down either way.
  const playback = usePlayback();
  const [casting, setCastingLocal] = useState(false);
  const mpvDurationRef = useRef(0);

  // Native picture-in-picture. Under mpv there is no <video>, so the browser's
  // requestPictureInPicture cannot be used — the shell moves its own surface
  // window instead. `state()` seeds the button on mount; `onState` keeps it in
  // step, including when PiP exits on its own (the window being closed).
  const nativePip = desktopPip();
  const [inNativePip, setInNativePip] = useState(false);
  useEffect(() => {
    if (!nativePip) return;
    let alive = true;
    void nativePip.state().then((m) => { if (alive) setInNativePip(m === "pip"); }).catch(() => {});
    const off = nativePip.onState((m) => setInNativePip(m === "pip"));
    return () => { alive = false; off(); };
  }, [nativePip]);

  // Native playback in the desktop shell. When `mpv.active` the browser
  // engines are never constructed at all — mpv decodes MKV / HEVC / AC-3
  // straight from the panel URL, so the server does no ffmpeg and no proxying
  // for desktop viewers. In a browser this is inert and everything below runs
  // exactly as it always has.
  const mpv = useMpvPlayback({
    nativeUrl,
    isLive,
    // Either source of truth: this player's own cast session, or the desktop
    // cast picker in the app chrome. Missing the second one left mpv playing
    // on the PC while the TV reported "Playing on Living".
    casting: casting || playback.casting,
    startAt: resumeAt || baseTime || undefined,
    onProgress: (t) => onProgress?.(t, mpvDurationRef.current),
    onEnded: () => onEnded?.(),
  });

  // Mirror mpv into the same state the controls already read, so the entire
  // control bar, scrub slider and keyboard shortcuts work unchanged rather
  // than growing a parallel desktop-only UI.
  useEffect(() => {
    if (!mpv.active) return;
    mpvDurationRef.current = mpv.duration;
    setPlaying(mpv.playing);
    setProgress({ current: mpv.currentTime, duration: mpv.duration });
  }, [mpv.active, mpv.playing, mpv.currentTime, mpv.duration]);

  useEffect(() => {
    if (mpv.active && mpv.error) setError(mpv.error);
  }, [mpv.active, mpv.error]);

  // Silence the browser engine whenever mpv takes over.
  //
  // Skipping engine *construction* was not enough: the singleton video element
  // is module-level and keeps playing whatever it last loaded. Going from Live
  // TV to a film left the channel's audio running underneath the film, and
  // because the element is no longer mounted (mpv renders the picture) it was
  // audible but invisible. Same unload the cast path performs, and for the same
  // reason — stop the fetcher pulling bytes from the panel as well as muting it.
  // A cast session started from the app chrome (the global picker) must stand
  // local playback down exactly like one started from this player's own button.
  // `enterCastMode` also sets the module-level `_isCasting` latch that the
  // autoplay guards check, so nothing restarts locally while the TV is playing.
  useEffect(() => {
    if (playback.casting) enterCastMode();
    else exitCastMode();
  }, [playback.casting]);

  useEffect(() => {
    if (!mpv.active && !playback.casting) return;
    const e = _engine;
    if (!e) return;
    try { e.videoEl.pause(); } catch { /* ignore */ }
    try { e.mpegts?.unload(); } catch { /* ignore */ }
    try { e.hls?.stopLoad(); } catch { /* ignore */ }
    try { e.videoEl.removeAttribute("src"); e.videoEl.load(); } catch { /* ignore */ }
  }, [mpv.active, playback.casting, nativeUrl]);
  // Mirror local casting state to the global PlaybackContext so the
  // mini-player can hide itself + the progress bar can hide its useless
  // local time. Use a ref-like pattern to avoid effect-loops.
  const setCasting = (c: boolean) => {
    setCastingLocal(c);
    playback.setCasting(c);
  };

  // Audio + subtitle track state (refreshed when the engine reports tracks)
  const [audioTracks, setAudioTracks] = useState<{ id: string; label: string; active: boolean }[]>([]);
  const [textTracks, setTextTracks] = useState<{ id: string; label: string; active: boolean }[]>([]);
  const [audioMenu, setAudioMenu] = useState(false);
  const [subMenu, setSubMenu] = useState(false);
  const [speedMenu, setSpeedMenu] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [inPip, setInPip] = useState(false);
  const [buffered, setBuffered] = useState(0);

  // Slider scrub state — when the user is actively dragging we show their
  // pending value rather than what the video reports (commit-on-release).
  const [scrubbing, setScrubbing] = useState(false);
  const [scrubValue, setScrubValue] = useState(0);

  function refreshTracks() {
    const e = _engine;
    if (!e) return;
    const v = e.videoEl;
    const hls = e.hls;
    if (hls) {
      const a = hls.audioTracks || [];
      const at = hls.audioTrack;
      setAudioTracks(
        a.map((t, i) => ({
          id: String(i),
          label: t.name || t.lang || `Audio ${i + 1}`,
          active: i === at,
        }))
      );
      const s = hls.subtitleTracks || [];
      const st = hls.subtitleTrack;
      setTextTracks(
        s.map((t, i) => ({
          id: String(i),
          label: t.name || t.lang || `Subtitles ${i + 1}`,
          active: i === st,
        })),
      );
      return;
    }
    type ATL = { length: number; [k: number]: { id: string; label: string; language: string; enabled: boolean } };
    const aTracks = (v as unknown as { audioTracks?: ATL }).audioTracks;
    if (aTracks && aTracks.length) {
      const out: { id: string; label: string; active: boolean }[] = [];
      for (let i = 0; i < aTracks.length; i++) {
        const t = aTracks[i];
        out.push({
          id: t.id || String(i),
          label: t.label || t.language || `Audio ${i + 1}`,
          active: t.enabled,
        });
      }
      setAudioTracks(out);
    } else {
      setAudioTracks([]);
    }
    const tTracks = v.textTracks;
    if (tTracks && tTracks.length) {
      const out: { id: string; label: string; active: boolean }[] = [];
      for (let i = 0; i < tTracks.length; i++) {
        const t = tTracks[i];
        out.push({
          id: String(i),
          label: t.label || t.language || `Subtitles ${i + 1}`,
          active: t.mode === "showing",
        });
      }
      setTextTracks(out);
    } else {
      setTextTracks([]);
    }
  }

  function selectAudio(idx: number) {
    const e = _engine;
    if (!e) return;
    const hls = e.hls;
    if (hls) {
      hls.audioTrack = idx;
    } else {
      type ATL = { length: number; [k: number]: { enabled: boolean } };
      const at = (e.videoEl as unknown as { audioTracks?: ATL }).audioTracks;
      if (at) for (let i = 0; i < at.length; i++) at[i].enabled = i === idx;
    }
    setAudioMenu(false);
    refreshTracks();
  }

  function selectSubtitle(idx: number | -1) {
    const e = _engine;
    if (!e) return;
    const hls = e.hls;
    if (hls) {
      hls.subtitleTrack = idx;
      hls.subtitleDisplay = idx >= 0;
    } else {
      const tt = e.videoEl.textTracks;
      for (let i = 0; i < tt.length; i++) tt[i].mode = i === idx ? "showing" : "disabled";
    }
    setSubMenu(false);
    refreshTracks();
  }

  // Cast SDK — load once, init CastContext with the default media receiver,
  // and listen for session state so we can pause local playback while
  // casting (otherwise we'd be double-streaming and chewing bandwidth).
  useEffect(() => {
    function init() {
      if (!window.cast?.framework || !window.chrome?.cast) return;
      const fw = window.cast.framework;
      try {
        const ctx = fw.CastContext.getInstance();
        ctx.setOptions({
          receiverApplicationId: window.chrome.cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID,
          autoJoinPolicy: window.chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED,
          resumeSavedSession: true,
        });
        setCastAvailable(true);
        const onState = (ev: unknown) => {
          const e = ev as { sessionState?: string };
          const SS = fw.SessionState;
          if (e.sessionState === SS.SESSION_STARTED || e.sessionState === SS.SESSION_RESUMED) {
            setCasting(true);
            enterCastMode();
          } else if (e.sessionState === SS.SESSION_ENDED || e.sessionState === SS.SESSION_START_FAILED) {
            setCasting(false);
            exitCastMode();
          }
        };
        ctx.addEventListener(fw.CastContextEventType.SESSION_STATE_CHANGED, onState);
      } catch {
        setCastAvailable(false);
      }
    }
    if (window.cast?.framework && window.chrome?.cast) {
      init();
      return;
    }
    if (!document.querySelector('script[data-cast-sender]')) {
      const s = document.createElement("script");
      s.src = "https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1";
      s.async = true;
      s.dataset.castSender = "1";
      document.head.appendChild(s);
    }
    window.__onGCastApiAvailable = (avail: boolean) => {
      if (avail) init();
    };
  }, []);

  // Manage <track> children on the singleton video for embedded subtitles.
  // The browser surfaces each <track> via video.textTracks once it loads;
  // our existing subtitle-picker (Captions button) reads from there. We
  // diff using a stringified key so a parent that hands us a fresh array
  // every render doesn't trip the effect on every render.
  const subsKey = JSON.stringify(
    (subtitles || []).map((s) => [s.url, s.lang, s.label, !!s.default])
  );
  useEffect(() => {
    const e = getEngine();
    const v = e.videoEl;
    Array.from(v.querySelectorAll("track")).forEach((t) => t.remove());
    if (subtitles && subtitles.length) {
      for (const s of subtitles) {
        const t = document.createElement("track");
        t.kind = "subtitles";
        t.src = s.url;
        t.srclang = s.lang || "und";
        t.label = s.label || s.lang || "Subtitles";
        if (s.default) t.default = true;
        v.appendChild(t);
      }
      // Auto-activate when the user explicitly picked one (single-track means
      // they came in via the modal — display it).
      const autoShowSingle = subtitles.length === 1;
      requestAnimationFrame(() => {
        const tt = v.textTracks;
        if (autoShowSingle && tt.length === 1) {
          tt[0].mode = "showing";
        }
        refreshTracks();
      });
    } else {
      refreshTracks();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subsKey]);

  // Mount: take ownership of the singleton video element. Unmount: return
  // it to the hidden limbo div so the engine keeps running while we
  // navigate between /watch and the mini-player.
  // After reparenting, if the user didn't explicitly pause and the video
  // is currently paused (browser auto-pause on reparent, or the engine
  // got stalled while in limbo), kick it back into play.
  useEffect(() => {
    const stage = stageRef.current;
    const e = getEngine();
    if (stage) stage.appendChild(e.videoEl);
    if (!_userPaused && !_isCasting && e.videoEl.paused) {
      e.videoEl.play().catch(() => { /* autoplay block — user gesture needed */ });
    }
    return () => {
      e.limbo.appendChild(e.videoEl);
    };
  }, []);

  // Engine load — only re-runs when the *resolved* URL or engine kind
  // actually changes. Re-mounting the component does NOT trigger a reload
  // (loadedKey check inside `loadInto` is a no-op on identical input).
  useEffect(() => {
    setError(null);
    // mpv owns playback in the desktop shell: constructing a browser engine
    // here would open a SECOND upstream connection for the same stream, and
    // with one slot per account that kills the playback it duplicates.
    if (mpv.active) return;
    if (!url) return;

    const kind = detectKind(url, engine);
    let chosenUrl = absolutize(url);
    let chosenKind: EngineKind = kind;

    if (kind === "ts" && isIOS() && altUrl) {
      chosenUrl = absolutize(altUrl);
      chosenKind = detectKind(altUrl, altEngine);
    }

    const e = getEngine();
    loadInto(e, chosenUrl, chosenKind, isLive, setError);

    if (resumeAt && resumeAt > 5) {
      const onLoaded = () => {
        try {
          if (e.videoEl.duration && resumeAt < e.videoEl.duration - 5) {
            e.videoEl.currentTime = resumeAt;
          }
        } catch { /* ignore */ }
      };
      e.videoEl.addEventListener("loadedmetadata", onLoaded, { once: true });
    }

    // Force-play when:
    //  - the parent asked for autoPlay (initial /watch mount), OR
    //  - the URL just changed (seek-via-reload in any context — including
    //    the mini-player). In the second case _userPaused was reset by
    //    loadInto and we want playback to continue from the new point.
    // Suppressed during casting so the cast receiver stays the sole player.
    // Suppressed for live mpegts.js streams: the preroll hold inside
    // loadInto() triggers play() once ~3s are buffered, otherwise the
    // browser would start playback on a near-empty MSE buffer.
    const isLiveTsEngine = chosenKind === "ts" && isLive;
    setAutoplayBlocked(false);
    if ((autoPlay || !_userPaused) && !_isCasting && !isLiveTsEngine) {
      e.videoEl.play().catch((err: unknown) => {
        const ee = err as { name?: string; message?: string };
        if (ee?.name === "NotSupportedError") {
          const ext = (chosenUrl.toLowerCase().split("?")[0].match(/\.([a-z0-9]+)$/) || [])[1];
          if (ext === "mkv") {
            setError(
              "MKV container can't be played natively in this browser. Try Microsoft Edge, Firefox, or download the file."
            );
          } else if (ext) {
            setError(`This format (.${ext}) isn't supported by your browser.`);
          } else {
            setError("This format isn't supported by your browser.");
          }
        } else if (ee?.name === "NotAllowedError") {
          // Autoplay policy block — typically a hard-refresh case where
          // there's no fresh user-gesture chain by the time we get here.
          // Surface a click-to-play overlay so the user has a visible
          // single-tap recovery path instead of staring at a paused frame.
          setAutoplayBlocked(true);
        }
      });
    }
  }, [url, altUrl, engine, altEngine, autoPlay, isLive, resumeAt, mpv.active]);

  // Apply persisted volume to the singleton video on mount.
  useEffect(() => {
    const e = getEngine();
    e.videoEl.volume = volume;
    e.videoEl.muted = volume === 0;
    setMuted(e.videoEl.muted);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Time tracking — listeners on the singleton video element. We re-attach
  // on every component mount so the slider stays in sync with the latest
  // baseTime / forceDuration props.
  useEffect(() => {
    // When mpv owns playback these listeners would fight it for the same
    // `progress` state: the singleton element still fires timeupdate from
    // whatever it last loaded, so the scrub bar oscillated between mpv's
    // position and the element's. mpv is the only source of truth here.
    if (mpv.active) return;
    const e = getEngine();
    const v = e.videoEl;
    setMuted(v.muted);
    setPlaying(!v.paused);
    setPlaybackRate(v.playbackRate);
    setInPip(document.pictureInPictureElement === v);
    setVolumeState(v.volume);
    const onTime = () => {
      const dur = forceDuration && forceDuration > 0 ? forceDuration : (baseTime + (v.duration || 0));
      const cur = baseTime + (v.currentTime || 0);
      setProgress({ current: cur, duration: dur });
      onProgress?.(cur, dur);
      // Buffered: read the END of the LAST buffered range. For non-seeked
      // playback this matches "how far ahead is loaded".
      try {
        const b = v.buffered;
        if (b && b.length) {
          setBuffered(baseTime + b.end(b.length - 1));
        }
      } catch {
        /* ignore */
      }
    };
    const onMeta = () => {
      refreshTracks();
      onTime();
    };
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onEnd = () => {
      setPlaying(false);
      onEnded?.();
    };
    const onErr = () => {
      // video.error.message can carry the codec hint that MSE rejected
      const msg = v.error?.message || "";
      if (isHevcCodecError(msg)) {
        setError(
          "This stream uses HEVC (H.265) video, which Chrome on this device can't decode. " +
          "Try Microsoft Edge or Safari, or a Mac/iPhone where HEVC is hardware-supported."
        );
      }
    };
    const onPip = () => setInPip(document.pictureInPictureElement === v);
    const onRate = () => setPlaybackRate(v.playbackRate);
    v.addEventListener("loadedmetadata", onMeta);
    v.addEventListener("timeupdate", onTime);
    v.addEventListener("progress", onTime);  // buffered ranges may change without timeupdate
    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);
    v.addEventListener("ended", onEnd);
    v.addEventListener("error", onErr);
    v.addEventListener("enterpictureinpicture", onPip);
    v.addEventListener("leavepictureinpicture", onPip);
    v.addEventListener("ratechange", onRate);
    onTime();  // sync immediately for re-mounts
    refreshTracks();
    return () => {
      v.removeEventListener("loadedmetadata", onMeta);
      v.removeEventListener("timeupdate", onTime);
      v.removeEventListener("progress", onTime);
      v.removeEventListener("play", onPlay);
      v.removeEventListener("pause", onPause);
      v.removeEventListener("ended", onEnd);
      v.removeEventListener("error", onErr);
      v.removeEventListener("enterpictureinpicture", onPip);
      v.removeEventListener("leavepictureinpicture", onPip);
      v.removeEventListener("ratechange", onRate);
    };
  }, [onProgress, onEnded, forceDuration, baseTime, mpv.active]);

  const togglePlay = () => {
    if (mpv.active) {
      // Set the same latch the browser path sets. Without it, pausing under
      // mpv left `_userPaused` false, so navigating away let the mini player's
      // engine autoplay — a paused stream restarting itself on Back.
      _userPaused = mpv.playing;
      mpv.toggle();
      return;
    }
    const e = _engine;
    if (!e) return;
    if (e.videoEl.paused) {
      _userPaused = false;
      // For TS pipes (live + catchup), the underlying stream may have stalled
      // while we were paused — a bare play() wouldn't resume it. mpegts.js
      // exposes play()/load() to re-arm the fetcher.
      if (e.mpegts) {
        try {
          e.mpegts.play();
        } catch {
          /* ignore */
        }
      }
      e.videoEl.play().catch(() => {});
    } else {
      _userPaused = true;
      e.videoEl.pause();
    }
  };

  const toggleMute = () => {
    const e = _engine;
    if (!e) return;
    e.videoEl.muted = !e.videoEl.muted;
    setMuted(e.videoEl.muted);
  };

  const setVolume = (v: number) => {
    const clamped = Math.max(0, Math.min(1, v));
    if (mpv.active) {
      mpv.setVolume(clamped);
      setVolumeState(clamped);
      setMuted(clamped === 0);
      try { localStorage.setItem("brainzr.volume", String(clamped)); } catch { /* ignore */ }
      return;
    }
    const e = _engine;
    if (!e) return;
    e.videoEl.volume = clamped;
    e.videoEl.muted = clamped === 0;
    setVolumeState(clamped);
    setMuted(clamped === 0);
    try {
      localStorage.setItem("brainzr.volume", String(clamped));
    } catch { /* ignore */ }
  };

  const toggleFullscreen = () => {
    const el = containerRef.current;
    if (!el) return;
    if (document.fullscreenElement) document.exitFullscreen();
    else el.requestFullscreen?.();
  };

  const togglePip = async () => {
    const e = _engine;
    if (!e) return;
    try {
      if (document.pictureInPictureElement === e.videoEl) {
        await document.exitPictureInPicture();
      } else {
        // Some browsers reject PiP if the video isn't currently playing or
        // hasn't loaded metadata yet — both rare in practice, swallow.
        await e.videoEl.requestPictureInPicture();
      }
    } catch { /* ignore */ }
  };

  const setRate = (r: number) => {
    const e = _engine;
    if (!e) return;
    e.videoEl.playbackRate = r;
    setPlaybackRate(r);
    setSpeedMenu(false);
  };

  // Native (in-buffer) seek — used when no `onSeek` callback is provided.
  const seekNative = (n: number) => {
    if (mpv.active) { mpv.seekTo(n); return; }
    const e = _engine;
    if (!e) return;
    e.videoEl.currentTime = Math.max(0, Math.min(e.videoEl.duration || n, n));
  };

  const onCast = async () => {
    // The desktop shell has no Google Cast Sender SDK — it speaks castv2
    // directly, and `CastPicker` in PageLayout is that UI. This button used to
    // call the SDK anyway, so in the desktop app it failed instantly with
    // "Cast: cast failed" and a Retry that could not ever work. The button is
    // hidden there now (see the control bar), and this is the belt-and-braces
    // guard in case it is reached another way.
    if (isDesktop()) {
      setError("Use the cast button at the top right — it finds devices on your network.");
      return;
    }
    if (!window.cast?.framework || !window.chrome?.cast) {
      setError("Cast not available — open this site in Chrome / Edge desktop.");
      return;
    }
    const ctx = window.cast.framework.CastContext.getInstance();
    const existing = ctx.getCurrentSession();
    if (existing && casting) {
      try {
        existing.endSession(true);
      } catch { /* ignore */ }
      return;
    }
    try {
      await ctx.requestSession();
      const session = ctx.getCurrentSession();
      if (!session) return;

      // Preference order:
      //   1. castUrl prop (cast-specific transcode endpoint — usually live HLS)
      //   2. .m3u8 alt URL (upstream HLS, if present)
      //   3. main url
      const altIsHls = (altUrl || "").toLowerCase().includes(".m3u8");
      const preferred = castUrl || (altIsHls ? altUrl! : url);
      let castMediaUrl = absolutize(preferred);
      if (castToken) {
        castMediaUrl += castMediaUrl.includes("?")
          ? `&t=${encodeURIComponent(castToken)}`
          : `?t=${encodeURIComponent(castToken)}`;
      }
      const lower = castMediaUrl.toLowerCase().split("?")[0];
      const mediaCt = lower.endsWith(".m3u8") ? "application/vnd.apple.mpegurl"
        : lower.endsWith(".webm") ? "video/webm"
        : lower.endsWith(".mkv") ? "video/x-matroska"
        : lower.endsWith(".ts") ? "video/mp2t"
        : "video/mp4";

      // Bare TS fallback only matters if NEITHER an HLS alt NOR our cast
      // transcode URL is available — which shouldn't happen for live now,
      // but guard anyway so the receiver never gets handed something that
      // would fall back to its idle screen.
      if (lower.endsWith(".ts") && !altIsHls && !castUrl) {
        setError(
          "Casting raw MPEG-TS isn't supported by the default Chromecast receiver. " +
          "This channel needs an HLS alternative or a custom receiver app."
        );
        return;
      }

      const info = new window.chrome.cast.media.MediaInfo(castMediaUrl, mediaCt);
      info.streamType = isLive
        ? window.chrome.cast.media.StreamType.LIVE
        : window.chrome.cast.media.StreamType.BUFFERED;

      const md = new window.chrome.cast.media.GenericMediaMetadata() as Record<string, unknown>;
      md.title = title || "8K Brainzr";
      md.subtitle = isLive ? "Live · 8K Brainzr" : "8K Brainzr";
      const brandIcon = absolutize("/icons/icon-512.png");
      const posterImg = poster ? absolutize(poster) : null;
      md.images = [
        new window.chrome.cast.Image(posterImg || brandIcon),
        new window.chrome.cast.Image(brandIcon),
      ];
      info.metadata = md;

      const req = new window.chrome.cast.media.LoadRequest(info);
      await session.loadMedia(req);

      _engine?.videoEl.pause();
      setCasting(true);
    } catch (e: unknown) {
      const m = (e as { description?: string; code?: string; message?: string })?.description
        || (e as { code?: string }).code
        || (e as { message?: string }).message
        || "cast failed";
      // Dismissing the device chooser rejects with the Cast SDK's CANCEL code
      // ('cancel'); older builds reject with the bare string. That is the user
      // declining to cast, not a failure — surfacing it left a sticky
      // "Cast: cancel" banner that could only be cleared by pressing Retry.
        if (isCastCancel(e)) return;
      setError(`Cast: ${m}`);
    }
  };

  // Transport overlay — focusable button row that opens on Enter / OK
  // (TV remote, keyboard). Mirrors the contract the Android client
  // enforces (spec §3) so muscle memory transfers between platforms.
  // Auto-hides 5 s after the last interaction.
  type TransportId = "restart" | "back30" | "playpause" | "fwd30" | "audio" | "cc";
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [overlayFocus, setOverlayFocus] = useState<TransportId>("playpause");
  const overlayTimerRef = useRef<number | null>(null);
  const audioMenuRef = useRef(audioMenu);
  const subMenuRef = useRef(subMenu);
  audioMenuRef.current = audioMenu;
  subMenuRef.current = subMenu;
  const transportButtons: TransportId[] = (() => {
    const out: TransportId[] = [];
    const showRestart = !isLive && !onSeek && progress.current >= 30;
    if (showRestart) out.push("restart");
    if (!isLive) out.push("back30");
    out.push("playpause");
    if (!isLive) out.push("fwd30");
    if (audioTracks.length > 1) out.push("audio");
    if (textTracks.length > 0 || onOpenSubsBrowser) out.push("cc");
    return out;
  })();

  // Keep `overlayFocus` valid whenever the visible button set changes (e.g.
  // restart drops out as duration rewinds < 30, or audio appears once tracks
  // load). Without this, focus could land on a hidden button silently.
  useEffect(() => {
    if (!transportButtons.includes(overlayFocus)) {
      const fallback: TransportId = isLive
        ? (transportButtons.includes("audio") ? "audio"
           : transportButtons.includes("cc") ? "cc" : "playpause")
        : "playpause";
      setOverlayFocus(fallback);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transportButtons.join("|"), isLive]);

  function bumpOverlayTimer() {
    if (overlayTimerRef.current !== null) {
      window.clearTimeout(overlayTimerRef.current);
    }
    overlayTimerRef.current = window.setTimeout(() => {
      setOverlayOpen(false);
      overlayTimerRef.current = null;
    }, 5000);
  }
  function openOverlay() {
    // Always start on play/pause so OK→OK is "open then toggle pause" — the
    // most common gesture. Without the reset, a previous arrow-nav to back30
    // sticks across closes, so OK on next open would unexpectedly seek -30.
    setOverlayFocus("playpause");
    setOverlayOpen(true);
    bumpOverlayTimer();
  }
  function closeOverlay() {
    setOverlayOpen(false);
    setOverlayFocus("playpause");
    if (overlayTimerRef.current !== null) {
      window.clearTimeout(overlayTimerRef.current);
      overlayTimerRef.current = null;
    }
  }
  useEffect(() => {
    return () => {
      if (overlayTimerRef.current !== null) {
        window.clearTimeout(overlayTimerRef.current);
      }
      if (tapTimerRef.current !== null) {
        window.clearTimeout(tapTimerRef.current);
      }
    };
  }, []);

  function activateTransport(id: TransportId) {
    const e = _engine;
    if (!e) return;
    bumpOverlayTimer();
    switch (id) {
      case "restart":
        if (onSeek) onSeek(0);
        else seekNative(0);
        break;
      case "back30":
        if (onSeek) onSeek(Math.max(0, progress.current - 30));
        else seekNative(e.videoEl.currentTime - 30);
        break;
      case "fwd30":
        if (onSeek) onSeek(progress.current + 30);
        else seekNative(e.videoEl.currentTime + 30);
        break;
      case "playpause":
        togglePlay();
        closeOverlay();
        break;
      case "audio":
        setAudioMenu((v) => !v);
        setSubMenu(false);
        break;
      case "cc":
        if (onOpenSubsBrowser) {
          onOpenSubsBrowser();
          closeOverlay();
          return;
        }
        setSubMenu((v) => !v);
        setAudioMenu(false);
        break;
    }
  }

  // Keyboard shortcuts. Order matters:
  //   1) `f`, `m` always work (handy global toggles).
  //   2) Enter — opens overlay if closed; activates focused button if open.
  //   3) Escape / Backspace — closes overlay if open; otherwise unhandled.
  //   4) ArrowLeft / ArrowRight — focus-nav while overlay is open;
  //      seek ±10 s otherwise. Spec says "held" jumps to ±30 s; on web we
  //      treat ev.repeat as the held signal.
  //   5) Space — toggle pause (matches existing behaviour).
  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      const e = _engine;
      if (!e) return;
      if (["INPUT", "TEXTAREA"].includes((ev.target as HTMLElement)?.tagName)) return;
      if (ev.key === "f" || ev.key === "F") { toggleFullscreen(); return; }
      if (ev.key === "m" || ev.key === "M") { toggleMute(); return; }
      if (ev.code === "Space") { ev.preventDefault(); togglePlay(); return; }
      if (ev.key === "Enter") {
        ev.preventDefault();
        if (!overlayOpen) {
          openOverlay();
        } else {
          activateTransport(overlayFocus);
        }
        return;
      }
      if (ev.key === "Escape" || ev.key === "Backspace") {
        if (overlayOpen) {
          ev.preventDefault();
          closeOverlay();
        }
        return;
      }
      if (ev.code === "ArrowRight" || ev.code === "ArrowLeft") {
        // Close pop-out menus that the existing controls opened — they
        // hijack arrow nav otherwise.
        if (audioMenuRef.current) setAudioMenu(false);
        if (subMenuRef.current) setSubMenu(false);
        if (overlayOpen) {
          ev.preventDefault();
          const idx = transportButtons.indexOf(overlayFocus);
          if (idx === -1) return;
          const next = ev.code === "ArrowRight"
            ? Math.min(transportButtons.length - 1, idx + 1)
            : Math.max(0, idx - 1);
          setOverlayFocus(transportButtons[next]);
          bumpOverlayTimer();
          return;
        }
        // Overlay closed → seek shortcut. Held arrow → 30 s step.
        const step = ev.repeat ? 30 : 10;
        if (ev.code === "ArrowRight") {
          if (onSeek) onSeek(progress.current + step);
          else seekNative(e.videoEl.currentTime + step);
        } else {
          if (onSeek) onSeek(Math.max(0, progress.current - step));
          else seekNative(e.videoEl.currentTime - step);
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progress.current, onSeek, overlayOpen, overlayFocus, transportButtons.join("|"), onOpenSubsBrowser]);

  const sliderValue = scrubbing ? scrubValue : progress.current;

  // ── Fullscreen chrome auto-hide ───────────────────────────────────────
  // In fullscreen the control bar has to behave like a real player: show on
  // any input, then fade out after a few seconds of stillness so the picture
  // is genuinely full-screen. Windowed playback keeps the bar pinned — it
  // sits inside the page layout there, where a vanishing bar reads as broken.
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [chromeVisible, setChromeVisible] = useState(true);
  const isPhone = useIsPhone();
  // Touch gesture state. Only the two gestures the web platform can actually
  // honour are implemented: tap-to-toggle-chrome and double-tap-to-seek.
  // Brightness is not exposed to web at all, and volume is read-only on iOS
  // Safari, so a vertical-drag volume gesture would silently do nothing on a
  // large share of phones — worse than not offering it.
  const tapRef = useRef<{ t: number; x: number } | null>(null);
  const tapTimerRef = useRef<number | null>(null);
  const [seekPulse, setSeekPulse] = useState<{ dir: -1 | 1; key: number } | null>(null);
  const chromeTimerRef = useRef<number | null>(null);

  useEffect(() => {
    const onFsChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
      // Entering and leaving both start from "visible" so the user can see
      // where the controls went before they fade.
      setChromeVisible(true);
    };
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  // Anything that still needs the pointer pins the chrome open: an open
  // pop-out menu, an in-progress scrub, a paused video, or a cast session
  // (where this bar is the only remaining UI).
  const chromePinned =
    !isFullscreen || !playing || scrubbing || audioMenu || subMenu || speedMenu || casting;

  useEffect(() => {
    const clear = () => {
      if (chromeTimerRef.current !== null) {
        window.clearTimeout(chromeTimerRef.current);
        chromeTimerRef.current = null;
      }
    };
    if (chromePinned) {
      clear();
      setChromeVisible(true);
      return;
    }
    const el = containerRef.current;
    if (!el) return;
    const bump = () => {
      setChromeVisible(true);
      clear();
      chromeTimerRef.current = window.setTimeout(() => {
        setChromeVisible(false);
        chromeTimerRef.current = null;
      }, 3000);
    };
    bump();
    // Listen on the container for pointer/touch, but on window for keys —
    // in fullscreen the focused element is often a control button, and key
    // events there never bubble through the container.
    const local = ["pointermove", "pointerdown", "touchstart", "wheel"] as const;
    local.forEach((e) => el.addEventListener(e, bump, { passive: true }));
    window.addEventListener("keydown", bump);
    return () => {
      local.forEach((e) => el.removeEventListener(e, bump));
      window.removeEventListener("keydown", bump);
      clear();
    };
  }, [chromePinned]);

  return (
    <div
      ref={containerRef}
      className={`relative w-full bg-black group ${
        // Native mode sizes the PICTURE to 16:9 and lets the docked bar add its
        // own height below. Keeping aspect-video on the outer box instead made
        // the picture region shorter than 16:9, so mpv pillarboxed it — the
        // black bars left and right.
        mpv.active ? "" : "aspect-video"
      } ${
        // mpv paints into a native window, and a native window renders above
        // ALL web content — so controls drawn over the video are clickable but
        // invisible. Hole-punching (mpv behind a transparent main window) was
        // tried and rejected: `transparent: true` strips WS_THICKFRAME on
        // Windows, losing edge-resize and Aero Snap. So in native mode the
        // controls are DOCKED below the picture instead of floating over it —
        // which is what VLC and MPC-HC do, and costs no window behaviour.
        mpv.active ? "flex flex-col" : ""
      } ${isFullscreen && !chromeVisible ? "cursor-none" : ""}`}
    >
      {/* The picture. In a browser the singleton <video> is mounted into this
          stage; in the desktop shell mpv paints into a native overlay window
          and this reports where to put it. Both keep the same layout slot, so
          the control bar and overlays above are identical either way. */}
      {mpv.active
        ? (
          // In fullscreen the picture takes everything that is left, so hiding
          // the bar genuinely fills the screen. Windowed, it stays 16:9.
          <div className={`relative ${isFullscreen ? "flex-1 min-h-0" : "aspect-video"}`}>
            <DesktopVideoSurface className="absolute inset-0" />
          </div>
        )
        : <div ref={stageRef} className="absolute inset-0" />}

      {/* Touch gesture layer — phone only, and never while a tap-to-play or
          cast overlay owns the surface. Sits above the video and below the
          controls so buttons keep working. */}
      {chrome && isPhone && !autoplayBlocked && !casting && (
        <div
          className="absolute inset-0 z-[5]"
          onPointerUp={(ev) => {
            if (ev.pointerType === "mouse") return;
            const rect = ev.currentTarget.getBoundingClientRect();
            const x = ev.clientX - rect.left;
            const now = Date.now();
            const prev = tapRef.current;
            // Second tap within 300ms and near the first: treat as double-tap.
            if (prev && now - prev.t < 300 && Math.abs(x - prev.x) < 60) {
              if (tapTimerRef.current !== null) {
                window.clearTimeout(tapTimerRef.current);
                tapTimerRef.current = null;
              }
              tapRef.current = null;
              const third = rect.width / 3;
              if (x < third || x > rect.width - third) {
                const dir: -1 | 1 = x < third ? -1 : 1;
                if (!isLive) {
                  const target = Math.max(0, progress.current + dir * 10);
                  if (onSeek) onSeek(target);
                  else seekNative(target);
                  setSeekPulse({ dir, key: now });
                  window.setTimeout(() => setSeekPulse(null), 450);
                }
              } else {
                togglePlay();
              }
              return;
            }
            tapRef.current = { t: now, x };
            // Single tap only resolves once the double-tap window closes,
            // otherwise every seek would also flash the chrome.
            tapTimerRef.current = window.setTimeout(() => {
              setChromeVisible((v) => !v);
              tapTimerRef.current = null;
              tapRef.current = null;
            }, 300);
          }}
        />
      )}

      {/* Seek feedback. Without it a double-tap on a still frame looks like
          nothing happened. */}
      {seekPulse && (
        <div
          key={seekPulse.key}
          className={`absolute top-1/2 -translate-y-1/2 z-[6] pointer-events-none flex items-center gap-1 px-4 py-2 rounded-pill bg-canvas/85 text-text-primary text-[13px] font-data ${
            seekPulse.dir < 0 ? "left-6" : "right-6"
          }`}
        >
          {seekPulse.dir < 0 ? "\u21ba 10s" : "10s \u21bb"}
        </div>
      )}
      {/* "Casting" overlay */}
      {casting && (
        <div className="absolute inset-0 bg-canvas flex flex-col items-center justify-center text-text-primary gap-3 px-6 text-center">
          <div className="w-14 h-14 rounded-full bg-accent-gold/20 text-accent-gold flex items-center justify-center">
            <Cast size={26} fill="currentColor" />
          </div>
          <p className="font-display text-[28px] tracking-[-0.5px]">Casting to your TV</p>
          <p className="text-[13px] font-ui text-text-secondary max-w-md">
            Local playback is paused so you're not double-streaming. Stop casting from the
            cast button above to resume here.
          </p>
        </div>
      )}
      {error && (
        <div className="absolute inset-0 bg-canvas/90 flex flex-col items-center justify-center text-text-secondary text-[14px] font-ui px-6 text-center gap-2">
          <p className="text-text-primary text-[16px] font-medium">Unable to play this stream</p>
          <p className="text-[13px] text-text-secondary max-w-md">{error}</p>
          <button
            onClick={() => location.reload()}
            className="mt-2 px-4 py-2 rounded-pill border border-muted/40 text-[12px] font-medium uppercase tracking-[0.04em] hover:border-accent-gold hover:text-accent-gold transition-fast"
          >
            Retry
          </button>
        </div>
      )}
      {!error && autoplayBlocked && (
        <button
          onClick={() => {
            const e = getEngine();
            setAutoplayBlocked(false);
            _userPaused = false;
            e.videoEl.play().catch((err: unknown) => {
              const ee = err as { name?: string };
              if (ee?.name === "NotAllowedError") setAutoplayBlocked(true);
            });
          }}
          className="absolute inset-0 flex items-center justify-center bg-canvas/40 backdrop-blur-[2px] group/tap"
          aria-label="Tap to play"
          title="Tap to play"
        >
          <span className="w-20 h-20 rounded-full bg-accent-gold/95 flex items-center justify-center group-hover/tap:scale-110 transition-fast shadow-2xl">
            <Play size={32} className="text-canvas ml-1" fill="currentColor" />
          </span>
          <span className="sr-only">Tap to play</span>
        </button>
      )}
      {/* Transport overlay — opens on Enter / OK. Buttons traverse with
          ArrowLeft / ArrowRight; OK activates the focused button. Mirrors
          the cross-platform contract in spec §3 so muscle memory carries
          over from the Android client. */}
      {overlayOpen && transportButtons.length > 0 && (
        <div
          className="absolute inset-0 flex items-end justify-center pb-28 pointer-events-none"
          aria-hidden={false}
        >
          <div
            className="pointer-events-auto flex items-center gap-3 px-3 py-2 rounded-2xl glass-panel shadow-2xl"
            role="toolbar"
            aria-label="Transport controls"
          >
            {transportButtons.map((id) => {
              const focused = id === overlayFocus;
              const ring = focused
                ? "ring-2 ring-accent-gold ring-offset-2 ring-offset-canvas/80 text-accent-gold"
                : "text-text-primary hover:text-accent-gold";
              const common =
                "w-14 h-14 rounded-xl flex items-center justify-center transition-fast bg-canvas/70 backdrop-blur-sm";
              const onClick = () => {
                setOverlayFocus(id);
                activateTransport(id);
              };
              const label = (() => {
                switch (id) {
                  case "restart": return "Restart from start";
                  case "back30": return "Back 30 seconds";
                  case "playpause": return playing ? "Pause" : "Play";
                  case "fwd30": return "Forward 30 seconds";
                  case "audio": return "Audio track";
                  case "cc": return "Subtitles";
                }
              })();
              const icon = (() => {
                switch (id) {
                  case "restart": return <RotateCcw size={22} />;
                  case "back30": return <ChevronsLeft size={26} />;
                  case "playpause":
                    return playing
                      ? <Pause size={22} fill="currentColor" />
                      : <Play size={22} fill="currentColor" />;
                  case "fwd30": return <ChevronsRight size={26} />;
                  case "audio": return <AudioLines size={22} />;
                  case "cc": return <Captions size={22} />;
                }
              })();
              return (
                <button
                  key={id}
                  type="button"
                  className={`${common} ${ring}`}
                  aria-label={label}
                  title={label}
                  onClick={onClick}
                  onMouseEnter={() => { setOverlayFocus(id); bumpOverlayTimer(); }}
                >
                  {icon}
                </button>
              );
            })}
          </div>
        </div>
      )}
      {/* Custom controls */}
      <div
        className={`p-3 z-10 pointer-events-none transition-opacity duration-300 ${
          chrome ? "" : "hidden"
        } ${
          mpv.active
            // Docked: in normal flow beneath the picture, never over it. A
            // native window cannot be drawn over, so hiding has to REMOVE the
            // bar from the layout rather than fade it — that is what lets the
            // picture expand to a genuinely full screen. Windowed it is always
            // pinned visible (chromePinned includes !isFullscreen); in
            // fullscreen it auto-hides and the picture grows to fill.
            ? (chromeVisible ? "relative shrink-0 bg-canvas opacity-100" : "hidden")
            : `absolute bottom-0 left-0 right-0 bg-gradient-to-t from-canvas/95 to-transparent ${
                chromeVisible ? "opacity-100" : "opacity-0 [&_*]:!pointer-events-none"
              }`
        }`}
        aria-hidden={!mpv.active && !chromeVisible}
      >
        {title && (
          <p className="mb-1 text-[14px] font-ui font-medium text-text-primary truncate">
            {title}
          </p>
        )}
        {url && (
          <p className="mb-2 text-[10px] font-mono uppercase tracking-[0.04em] text-text-secondary truncate">
            {streamSourceLabel(mpv.active && nativeUrl ? nativeUrl : url, mpv.active)}
          </p>
        )}
        {!isLive && !casting && progress.duration > 0 && (
          <div className="mb-2 flex items-center gap-2 text-[11px] font-mono text-text-secondary pointer-events-auto">
            <span className="tabular-nums">{fmt(sliderValue)}</span>
            <div className={`relative flex-1 h-5 flex items-center group/slider ${!seekable ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}>
              {/* base track */}
              <div className="absolute left-0 right-0 h-1.5 rounded-full bg-line top-1/2 -translate-y-1/2 pointer-events-none" />
              {/* buffered ahead — paler than the played portion so it reads as "loaded but not yet played" */}
              <div
                className="absolute left-0 h-1.5 rounded-full bg-line-strong top-1/2 -translate-y-1/2 pointer-events-none transition-[width] duration-150"
                style={{ width: `${Math.max(0, Math.min(100, (buffered / progress.duration) * 100))}%` }}
              />
              {/* played */}
              <div
                className="absolute left-0 h-1.5 rounded-full bg-accent-gold top-1/2 -translate-y-1/2 pointer-events-none"
                style={{ width: `${Math.max(0, Math.min(100, (sliderValue / progress.duration) * 100))}%` }}
              />
              {/* thumb */}
              <div
                className="absolute w-3.5 h-3.5 rounded-full bg-accent-gold shadow-md top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none transition-transform group-hover/slider:scale-125"
                style={{ left: `${Math.max(0, Math.min(100, (sliderValue / progress.duration) * 100))}%` }}
              />
              {/* invisible range input on top — captures keyboard + pointer */}
              <input
                type="range"
                min={0}
                max={progress.duration}
                value={sliderValue}
                step={0.1}
                disabled={!seekable}
                onChange={(ev) => {
                  if (!seekable) return;
                  const v = Number(ev.target.value);
                  if (onSeek) {
                    setScrubbing(true);
                    setScrubValue(v);
                  } else {
                    seekNative(v);
                  }
                }}
                onPointerUp={() => {
                  if (!seekable) return;
                  if (onSeek && scrubbing) onSeek(scrubValue);
                  setScrubbing(false);
                }}
                onPointerCancel={() => setScrubbing(false)}
                onKeyUp={(ev) => {
                  if (!seekable) return;
                  if (onSeek && scrubbing && (ev.key === "ArrowLeft" || ev.key === "ArrowRight" || ev.key === "Home" || ev.key === "End")) {
                    onSeek(scrubValue);
                    setScrubbing(false);
                  }
                }}
                aria-label="Seek"
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
              />
            </div>
            <span className="tabular-nums">{fmt(progress.duration)}</span>
          </div>
        )}
        <div className="flex items-center gap-2 pointer-events-auto">
          <button onClick={togglePlay} className="text-text-primary hover:text-accent-gold p-2">
            {playing ? <Pause size={18} fill="currentColor" /> : <Play size={18} fill="currentColor" />}
          </button>
          <div className="flex items-center gap-1 group/vol">
            <button onClick={toggleMute} className="text-text-primary hover:text-accent-gold p-2" title={muted ? "Unmute (M)" : "Mute (M)"}>
              {muted || volume === 0 ? <VolumeX size={18} /> : <Volume2 size={18} />}
            </button>
            {/* Volume slider — collapses on small screens, expands on hover/desktop */}
            <div className="relative h-5 flex items-center w-0 group-hover/vol:w-20 sm:w-20 transition-[width] duration-200 overflow-hidden">
              <div className="absolute left-0 right-0 h-1.5 rounded-full bg-line top-1/2 -translate-y-1/2 pointer-events-none" />
              <div
                className="absolute left-0 h-1.5 rounded-full bg-accent-gold top-1/2 -translate-y-1/2 pointer-events-none"
                style={{ width: `${(muted ? 0 : volume) * 100}%` }}
              />
              <input
                type="range"
                min={0}
                max={1}
                step={0.02}
                value={muted ? 0 : volume}
                onChange={(ev) => setVolume(Number(ev.target.value))}
                aria-label="Volume"
                className="absolute inset-0 w-full opacity-0 cursor-pointer"
              />
            </div>
          </div>
          {isLive && (
            <span className="text-[10px] font-ui font-bold uppercase tracking-[0.06em] px-1.5 py-0.5 rounded-pill bg-danger text-text-primary">
              ● Live
            </span>
          )}
          <div className="flex-1" />
          {audioTracks.length > 1 && (
            <div className="relative">
              <button
                onClick={() => { setAudioMenu((v) => !v); setSubMenu(false); }}
                className="text-text-primary hover:text-accent-gold p-2"
                title="Audio track"
                aria-pressed={audioMenu}
              >
                <AudioLines size={18} />
              </button>
              {audioMenu && (
                <div className="absolute bottom-full right-0 mb-2 glass-panel rounded-xl p-1 min-w-[180px] z-10">
                  {audioTracks.map((t, i) => (
                    <button
                      key={t.id}
                      onClick={() => selectAudio(i)}
                      className={`w-full text-left px-3 py-1.5 rounded-lg text-[12px] font-ui transition-fast ${
                        t.active ? "bg-accent-gold/20 text-accent-gold" : "text-text-primary hover:bg-card-hover"
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {(textTracks.length > 0 || onOpenSubsBrowser) && (
            <div className="relative">
              <button
                onClick={() => {
                  if (onOpenSubsBrowser) {
                    // Parent owns the modal — they handle it.
                    onOpenSubsBrowser();
                    return;
                  }
                  setSubMenu((v) => !v);
                  setAudioMenu(false);
                }}
                className={`p-2 transition-fast ${
                  textTracks.some((t) => t.active)
                    ? "text-accent-gold"
                    : "text-text-primary hover:text-accent-gold"
                }`}
                title="Subtitles"
                aria-pressed={subMenu}
              >
                <Captions size={18} />
              </button>
              {!onOpenSubsBrowser && subMenu && (
                <div className="absolute bottom-full right-0 mb-2 glass-panel rounded-xl p-1 min-w-[180px] z-10">
                  <button
                    onClick={() => selectSubtitle(-1)}
                    className={`w-full text-left px-3 py-1.5 rounded-lg text-[12px] font-ui transition-fast ${
                      !textTracks.some((t) => t.active) ? "bg-accent-gold/20 text-accent-gold" : "text-text-primary hover:bg-card-hover"
                    }`}
                  >
                    Off
                  </button>
                  {textTracks.map((t, i) => (
                    <button
                      key={t.id}
                      onClick={() => selectSubtitle(i)}
                      className={`w-full text-left px-3 py-1.5 rounded-lg text-[12px] font-ui transition-fast ${
                        t.active ? "bg-accent-gold/20 text-accent-gold" : "text-text-primary hover:bg-card-hover"
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {!isLive && (
            <div className="relative">
              <button
                onClick={() => { setSpeedMenu((v) => !v); setAudioMenu(false); setSubMenu(false); }}
                className={`p-2 transition-fast inline-flex items-center gap-1 ${
                  playbackRate !== 1 ? "text-accent-gold" : "text-text-primary hover:text-accent-gold"
                }`}
                title="Playback speed"
                aria-pressed={speedMenu}
              >
                <Gauge size={18} />
                {playbackRate !== 1 && (
                  <span className="text-[10px] font-mono">{playbackRate}×</span>
                )}
              </button>
              {speedMenu && (
                <div className="absolute bottom-full right-0 mb-2 glass-panel rounded-xl p-1 min-w-[120px] z-10">
                  {[0.5, 0.75, 1, 1.25, 1.5, 1.75, 2].map((r) => (
                    <button
                      key={r}
                      onClick={() => setRate(r)}
                      className={`w-full text-left px-3 py-1.5 rounded-lg text-[12px] font-mono transition-fast ${
                        playbackRate === r ? "bg-accent-gold/20 text-accent-gold" : "text-text-primary hover:bg-card-hover"
                      }`}
                    >
                      {r}×{r === 1 ? "  (Normal)" : ""}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {/* Under mpv there is no <video>, so the browser API cannot be used;
              the shell moves its own surface window instead. Same button, same
              place — only the mechanism differs. */}
          {mpv.active && nativePip && (
            <button
              onClick={() => { void (inNativePip ? nativePip.leave() : nativePip.enter()); }}
              className={`p-2 transition-fast ${inNativePip ? "text-accent-gold" : "text-text-primary hover:text-accent-gold"}`}
              title={inNativePip ? "Exit picture-in-picture" : "Picture-in-picture"}
              aria-pressed={inNativePip}
            >
              <PictureInPicture2 size={18} />
            </button>
          )}
          {!mpv.active && document && "pictureInPictureEnabled" in document && (document as Document).pictureInPictureEnabled && (
            <button
              onClick={togglePip}
              className={`p-2 transition-fast ${inPip ? "text-accent-gold" : "text-text-primary hover:text-accent-gold"}`}
              title={inPip ? "Exit picture-in-picture" : "Picture-in-picture"}
              aria-pressed={inPip}
            >
              <PictureInPicture2 size={18} />
            </button>
          )}
          {castAvailable && !isDesktop() && (
            <button
              onClick={onCast}
              className={`p-2 transition-fast ${casting ? "text-accent-gold" : "text-text-primary hover:text-accent-gold"}`}
              title={casting ? "Stop casting" : "Cast to a device"}
              aria-pressed={casting}
            >
              <Cast size={18} fill={casting ? "currentColor" : "none"} />
            </button>
          )}
          <button onClick={toggleFullscreen} className="text-text-primary hover:text-accent-gold p-2" title="Fullscreen (F)">
            <Maximize size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Drop the singleton player engine entirely — used by the mini-player Close
 * (X) button. Once destroyed, the next mounted Player rebuilds it.
 */
export function destroyPlayer() {
  if (!_engine) return;
  destroyEngines(_engine);
  try {
    _engine.videoEl.pause();
    _engine.videoEl.removeAttribute("src");
    _engine.videoEl.load();
  } catch { /* ignore */ }
  _engine.loadedKey = "";
  // Keep the video element in DOM (limbo) so subsequent Player mounts can
  // reuse it without rebuilding the cast SDK / track listeners.
}

function fmt(s: number): string {
  if (!isFinite(s)) return "0:00";
  const t = Math.floor(s);
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const sec = t % 60;
  return h > 0 ? `${h}:${m.toString().padStart(2, "0")}:${sec.toString().padStart(2, "0")}` : `${m}:${sec.toString().padStart(2, "0")}`;
}
